import { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar, FileText, GraduationCap, Shield, MessageSquare, Handshake, Users, Clock, CheckCircle2, XCircle, AlertCircle, BookOpen, AlertTriangle, ChevronRight, CalendarOff } from "lucide-react";
import RiepilogoAssenzePanel from "./RiepilogoAssenzePanel";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function OperatorHome({ user, reports, corsiAssegnati, progressiUtente, richiesteAssenze, comunicazioni, teamMembers, onNavigate }) {
  const [mandatoryPending, setMandatoryPending] = useState([]);

  useEffect(() => {
    const loadProcedures = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/procedure`, { headers: { Authorization: `Bearer ${token}` } });
        const procs = res.data || [];
        const pending = procs.filter(p => {
          if (!p.is_mandatory) return false;
          const myRead = (p.reads || []).find(r => r.user_id === user.id);
          if (!myRead) return true;
          return myRead.versione !== p.versione;
        });
        setMandatoryPending(pending);
      } catch (error) {
        console.error('Errore caricamento procedure obbligatorie:', error);
      }
    };
    loadProcedures();
  }, [user.id]);

  const reportCount = reports?.length || 0;

  // Corsi da completare
  const corsiDaCompletare = (corsiAssegnati || []).filter(corso => {
    const progresso = (progressiUtente || []).find(p => p.corso_id === corso.id);
    return !progresso?.completato;
  });

  // Comunicazioni non lette
  const comunicazioniNonLette = (comunicazioni || []).filter(c => !c.letto);

  // Richieste assenze recenti
  const richiesteRecenti = (richiesteAssenze || []).slice(0, 4);
  const richiestePending = (richiesteAssenze || []).filter(r => r.stato === 'pending');

  // Team pending requests (for coordinators)
  const teamPendingRequests = user.is_coordinatore 
    ? (richiesteAssenze || []).filter(r => r.stato === 'pending')
    : [];

  const statusConfig = {
    pending: { label: 'In Attesa', icon: Clock, color: 'text-amber-600 bg-amber-50' },
    approved: { label: 'Approvata', icon: CheckCircle2, color: 'text-emerald-600 bg-emerald-50' },
    rejected: { label: 'Rifiutata', icon: XCircle, color: 'text-red-600 bg-red-50' }
  };

  return (
    <div className="space-y-6" data-testid="operator-home">
      {/* Welcome */}
      <div>
        <h2 className="text-2xl font-bold text-gray-900">
          Ciao, {user.first_name || user.full_name}
        </h2>
        <p className="text-sm text-gray-500 mt-0.5">
          {[user.figura_professionale, user.asl_riferimento].filter(Boolean).join(' · ')}
          {user.is_coordinatore && <span className="ml-2 px-1.5 py-0.5 bg-amber-100 text-amber-700 text-[10px] font-medium rounded">Coordinatore</span>}
        </p>
      </div>

      {/* Mandatory procedures banner */}
      {mandatoryPending.length > 0 && (
        <button
          type="button"
          onClick={() => onNavigate('procedure')}
          className="w-full flex items-center gap-3 p-3 rounded-lg bg-red-50 border border-red-200 hover:bg-red-100 transition-colors text-left"
          data-testid="mandatory-procedures-banner"
        >
          <div className="h-9 w-9 rounded-full bg-red-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-red-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-red-800">
              {mandatoryPending.length === 1
                ? '1 procedura obbligatoria da leggere'
                : `${mandatoryPending.length} procedure obbligatorie da leggere`}
            </div>
            <div className="text-xs text-red-600 truncate">
              {mandatoryPending.slice(0, 2).map(p => p.titolo).join(' · ')}
              {mandatoryPending.length > 2 && ` · +${mandatoryPending.length - 2} altre`}
            </div>
          </div>
          <ChevronRight className="w-4 h-4 text-red-500 shrink-0" />
        </button>
      )}

      {/* Quick action: Richiesta assenza (link diretto al dialog del tab Turni) */}
      <button
        type="button"
        onClick={() => {
          sessionStorage.setItem('openAbsenceDialog', '1');
          onNavigate('turni');
        }}
        className="w-full flex items-center gap-3 p-3 rounded-lg bg-cyan-50 border border-cyan-200 hover:bg-cyan-100 transition-colors text-left"
        data-testid="absence-request-cta"
      >
        <div className="h-9 w-9 rounded-full bg-cyan-100 flex items-center justify-center shrink-0">
          <CalendarOff className="w-4 h-4 text-cyan-600" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-cyan-900">
            Richiedi assenza
          </div>
          <div className="text-xs text-cyan-700">
            Ferie, permessi, malattia · ore o giornate intere
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-cyan-500 shrink-0" />
      </button>

      {/* Coordinator: Team Section */}
      {user.is_coordinatore && (
        <Card className="border border-amber-200 bg-amber-50/30" data-testid="team-section">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-amber-600" />
                <h3 className="font-semibold text-sm text-gray-900">Il mio Team</h3>
              </div>
              <button onClick={() => onNavigate('turni')} className="text-xs text-cyan-600 hover:text-cyan-700 font-medium">
                Gestisci turni →
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              <div className="bg-white rounded-lg p-3 border border-gray-100 text-center">
                <div className="text-2xl font-bold text-gray-900">{(teamMembers || []).length}</div>
                <div className="text-[11px] text-gray-500">Operatori</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-100 text-center cursor-pointer hover:border-amber-200" onClick={() => onNavigate('turni')}>
                <div className="text-2xl font-bold text-amber-600">{teamPendingRequests.length}</div>
                <div className="text-[11px] text-gray-500">Richieste da gestire</div>
              </div>
              <div className="bg-white rounded-lg p-3 border border-gray-100 text-center col-span-2 sm:col-span-1">
                <div className="flex flex-wrap gap-1 justify-center">
                  {(teamMembers || []).slice(0, 5).map(m => (
                    <span key={m.id} className="w-7 h-7 rounded-full bg-gray-100 text-[10px] font-bold text-gray-600 flex items-center justify-center" title={m.full_name}>
                      {(m.first_name?.[0] || '').toUpperCase()}{(m.last_name?.[0] || '').toUpperCase()}
                    </span>
                  ))}
                  {(teamMembers || []).length > 5 && (
                    <span className="w-7 h-7 rounded-full bg-gray-200 text-[10px] font-bold text-gray-500 flex items-center justify-center">
                      +{(teamMembers || []).length - 5}
                    </span>
                  )}
                </div>
                <div className="text-[11px] text-gray-500 mt-1">Membri</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Riepilogo annuale assenze del team (solo coordinatore) */}
      {user.is_coordinatore && (
        <RiepilogoAssenzePanel />
      )}

      {/* Quick access grid with live data */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        <Card
          className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => onNavigate('turni')}
          data-testid="home-card-turni"
        >
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-xl bg-cyan-100 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <Calendar className="h-4 w-4 text-cyan-600" />
            </div>
            <p className="font-semibold text-sm text-gray-900">Turni</p>
            <p className="text-xs text-gray-500 mt-0.5">Calendario turni</p>
          </CardContent>
        </Card>

        <Card
          className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => onNavigate('reports')}
          data-testid="home-card-report"
        >
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-xl bg-amber-100 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <FileText className="h-4 w-4 text-amber-600" />
            </div>
            <p className="font-semibold text-sm text-gray-900">Report</p>
            <p className="text-xs text-gray-500 mt-0.5">{reportCount} inviati</p>
          </CardContent>
        </Card>

        <Card
          className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer group relative"
          onClick={() => onNavigate('formazione')}
          data-testid="home-card-formazione"
        >
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-xl bg-violet-100 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <GraduationCap className="h-4 w-4 text-violet-600" />
            </div>
            <p className="font-semibold text-sm text-gray-900">Formazione</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {corsiDaCompletare.length > 0 ? `${corsiDaCompletare.length} da completare` : 'Tutti completati'}
            </p>
            {corsiDaCompletare.length > 0 && (
              <span className="absolute top-2 right-2 h-2 w-2 bg-violet-500 rounded-full" />
            )}
          </CardContent>
        </Card>

        <Card
          className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer group relative"
          onClick={() => onNavigate('procedure')}
          data-testid="home-card-procedure"
        >
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-xl bg-indigo-100 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <BookOpen className="h-4 w-4 text-indigo-600" />
            </div>
            <p className="font-semibold text-sm text-gray-900">Procedure</p>
            <p className="text-xs text-gray-500 mt-0.5">Protocolli operativi</p>
          </CardContent>
        </Card>

        <Card
          className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer group relative"
          onClick={() => onNavigate('comunicazioni')}
          data-testid="home-card-comunicazioni"
        >
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-xl bg-blue-100 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <MessageSquare className="h-4 w-4 text-blue-600" />
            </div>
            <p className="font-semibold text-sm text-gray-900">Comunicazioni</p>
            <p className="text-xs text-gray-500 mt-0.5">
              {comunicazioniNonLette.length > 0 ? `${comunicazioniNonLette.length} non lette` : 'Nessuna nuova'}
            </p>
            {comunicazioniNonLette.length > 0 && (
              <span className="absolute top-2 right-2 h-4 min-w-[16px] px-1 bg-red-500 text-white text-[9px] font-bold rounded-full flex items-center justify-center">
                {comunicazioniNonLette.length}
              </span>
            )}
          </CardContent>
        </Card>

        <Card
          className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => onNavigate('privacy')}
          data-testid="home-card-informative"
        >
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-xl bg-emerald-100 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <Shield className="h-4 w-4 text-emerald-600" />
            </div>
            <p className="font-semibold text-sm text-gray-900">Informative</p>
            <p className="text-xs text-gray-500 mt-0.5">Privacy e policy</p>
          </CardContent>
        </Card>

        <Card
          className="border-0 shadow-md hover:shadow-lg transition-all cursor-pointer group"
          onClick={() => onNavigate('convenzioni')}
          data-testid="home-card-convenzioni"
        >
          <CardContent className="p-4">
            <div className="h-9 w-9 rounded-xl bg-pink-100 flex items-center justify-center mb-2 group-hover:scale-110 transition-transform">
              <Handshake className="h-4 w-4 text-pink-600" />
            </div>
            <p className="font-semibold text-sm text-gray-900">Convenzioni</p>
            <p className="text-xs text-gray-500 mt-0.5">Accordi attivi</p>
          </CardContent>
        </Card>
      </div>

      {/* Le mie richieste assenze */}
      {richiesteRecenti.length > 0 && (
        <div data-testid="richieste-section">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-700">Le mie richieste</h3>
            <button onClick={() => onNavigate('turni')} className="text-xs text-cyan-600 hover:text-cyan-700 font-medium">
              Vedi tutte →
            </button>
          </div>
          <div className="space-y-2">
            {richiesteRecenti.map(r => {
              const config = statusConfig[r.stato] || statusConfig.pending;
              const Icon = config.icon;
              return (
                <div key={r.id} className="flex items-center gap-3 p-3 bg-white rounded-lg border border-gray-100">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center shrink-0 ${config.color}`}>
                    <Icon className="w-3.5 h-3.5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 capitalize">{r.tipo}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${config.color}`}>{config.label}</span>
                    </div>
                    <div className="text-xs text-gray-500">
                      {new Date(r.data_inizio).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })} — {new Date(r.data_fine).toLocaleDateString('it-IT', { day: 'numeric', month: 'short' })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Empty state for richieste */}
      {richiestePending.length === 0 && richiesteRecenti.length === 0 && (
        <div className="text-center py-4">
          <p className="text-xs text-gray-400">Nessuna richiesta di assenza recente</p>
          <button onClick={() => onNavigate('turni')} className="text-xs text-cyan-600 hover:text-cyan-700 font-medium mt-1">
            Richiedi un'assenza →
          </button>
        </div>
      )}
    </div>
  );
}
